import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import usePunishmentState, { IPunishment } from '@/states/punishment.state';
import useTenantState from '@/states/tenant.state';
import useOrgChartState from '@/states/organization.state';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';

export const usePunishmentLogic = () => {
  const formControl = useForm<IPunishment>({ defaultValues: { name: '', value: '', description: '' } });
  const { handleSubmit, reset } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [allPublished, setAllPublished] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [formMode, setFormMode] = useState<'add' | 'edit' | null>(null);
  const [selectedRow, setSelectedRow] = useState<IPunishment | null>(null);
  const [sortedPunishments, setSortedPunishments] = useState<IPunishment[]>([]);
  const [pageConfig, setPageConfig] = useState<DataConfig>({
    page: 0,
    limit: 10,
    orderBy: 'org_chart_node.name',
    order: 'asc',
  });

  const { currentTenant } = useTenantState();
  const { orgCharts, fetchOrgChartTree } = useOrgChartState();
  const { punishments, getPunishments, createPunishment, updatePunishment, deletePunishment, bulkPublishPunishment, isLoading } =
    usePunishmentState();

  const flattenTree = (nodes: any[]): any[] => {
    const result: any[] = [];
    nodes.forEach(node => {
      result.push(node);
      if (node.children?.length > 0) {
        result.push(...flattenTree(node.children));
      }
    });
    return result;
  };

  useEffect(() => {
    setAllPublished(punishments.length > 0 && punishments.every(item => item.is_published === true));
  }, [punishments]);

  useEffect(() => {
    if (!currentTenant?.id) return;
    const fetchData = async () => {
      try {
        const orgChartTree = await fetchOrgChartTree(currentTenant.id);
        if (orgChartTree) await getPunishments(currentTenant.id);
      } catch (err) {
        console.error('Failed to fetch punishments:', err);
        toast.error('Failed to fetch punishments');
      }
    };
    fetchData();
  }, [currentTenant?.id]);

  useEffect(() => {
    if (!punishments.length) return;
    const dir = pageConfig.order === 'asc' ? 1 : -1;
    const sorted = [...punishments].sort((a, b) => {
      if (pageConfig.orderBy === 'org_chart_node.name') {
        const aVal = a.org_chart_node?.name ?? '';
        const bVal = b.org_chart_node?.name ?? '';
        return aVal.localeCompare(bVal) * dir;
      }
      const key = pageConfig.orderBy as keyof IPunishment;
      const aVal = a[key] ?? '';
      const bVal = b[key] ?? '';
      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });
    setSortedPunishments(sorted);
  }, [pageConfig.orderBy, pageConfig.order, punishments]);

  const filteredPunishments = sortedPunishments.filter(item => {
    const term = searchTerm.toLowerCase();
    const matchText =
      (item.name ?? '').toLowerCase().includes(term) ||
      (item.value ?? '').toLowerCase().includes(term) ||
      (item.description ?? '').toLowerCase().includes(term) ||
      (item.org_chart_node?.name ?? '').toLowerCase().includes(term);
    const matchRole = selectedRole ? String(item.org_chart_node_id) === selectedRole : true;
    const matchStatus = !selectedStatus || (selectedStatus === 'Published' ? item.is_published : !item.is_published);
    return matchText && matchRole && matchStatus;
  });

  const handleSave = async (data: IPunishment) => {
    if (!currentTenant?.id) return;
    try {
      const payload: Partial<IPunishment> = {
        org_chart_node_id: Number(data.org_chart_node_id),
        name: data.name,
        value: data.value,
        description: data.description,
        is_published: data.is_published ?? false,
        attachment_file: data.attachment_file,
      };

      if (formMode === 'edit' && selectedRow?.id) {
        await updatePunishment(currentTenant.id, selectedRow.id, payload);
      } else {
        await createPunishment(currentTenant.id, payload);
      }

      setFormMode(null);
      setSelectedRow(null);
      reset({ name: '', value: '', description: '', org_chart_node_id: undefined });
      await getPunishments(currentTenant.id);
    } catch {
      toast.error('Failed to save punishment');
    }
  };

  const handleDelete = async (row: IPunishment) => {
    if (!currentTenant?.id || !row.id) return;
    await deletePunishment(currentTenant.id, row.id);
    await getPunishments(currentTenant.id);
  };

  const bulkPublishPunishmentHandler = async (value: boolean) => {
    if (!currentTenant?.id) return;
    await bulkPublishPunishment(currentTenant.id, value);
    await getPunishments(currentTenant.id);
  };

  const onEdit = (row: IPunishment) => {
    let parsedAttachment = null;
    try {
      const files = row.attachment_file ? JSON.parse(row.attachment_file) : [];
      parsedAttachment = files.length > 0 ? files[0] : null;
    } catch {
      parsedAttachment = null;
    }

    reset({
      ...row,
      attachment_file: parsedAttachment
        ? [
          {
            name: parsedAttachment.original_name || parsedAttachment.path?.split('/').pop() || 'Unnamed File',
            url: parsedAttachment.url || parsedAttachment.path,
            mime_type: parsedAttachment.mime_type,
            size: parsedAttachment.size,
          },
        ]
        : [],
    });

    setFormMode('edit');
    setSelectedRow(row);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRole('');
    setSelectedStatus('');
  };

  const allRoles = flattenTree(orgCharts);
  const roleOptions = allRoles.map(node => ({
    label: node.name,
    value: String(node.id),
  }));

  return {
    formControl,
    formMode,
    formRef,
    handleSave: handleSubmit(handleSave),
    handleDelete,
    onEdit,
    reset,
    isLoading,
    allPublished,
    filteredPunishments,
    pageConfig,
    setPageConfig,
    bulkPublishPunishmentHandler,
    searchTerm,
    setSearchTerm,
    selectedRole,
    setSelectedRole,
    selectedStatus,
    setSelectedStatus,
    clearFilters,
    orgCharts,
    roleOptions,
    setFormMode,
  };
};
